import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ChevronRight, Download, ScrollText } from 'lucide-react';
import { formatCents, formatDate, type PropertyEvent } from '@vuokramani/shared';
import { Link, useLocation, useParams } from 'react-router';
import { PageHeader } from '../../components/page.tsx';
import { Button, buttonClass, EmptyState, Panel, Splash } from '../../components/ui.tsx';
import { monthLabel } from '../../lib/invoices.ts';
import { leaseLogPdfUrl, propertyLogPdfUrl, useLeaseLog } from '../../lib/logs.ts';
import { propertyTitle, useProperty } from '../../lib/properties.ts';
import { useEvents } from '../../lib/tenancy.ts';
import s from './PropertyLog.module.css';

/**
 * Vuokrasuhteiden lokit todisteeksi. Loki avataan Tili-sivulta: vuokranantaja asunnon tai
 * vuokrasuhteen lokin, vuokralainen oman vuokrasuhteensa (myös päättyneen säilytysajan
 * verran). Kirjaukset syntyvät automaattisesti, eikä niitä voi muokata tai poistaa.
 * Samat tekstit PDF:ssä: apps/api/src/services/logText.ts.
 */

/** Tapahtumat päivittäin ryhmiteltyinä, uusin ensin. */
export function LogEvents({ events }: { events: PropertyEvent[] }) {
  const { i18n } = useTranslation();
  const days: Array<{ day: string; events: PropertyEvent[] }> = [];
  for (const e of events) {
    const day = localDay(e.createdAt);
    const last = days.at(-1);
    if (last?.day === day) last.events.push(e);
    else days.push({ day, events: [e] });
  }
  return (
    <>
      {days.map(({ day, events: list }) => (
        <section key={day} className={s.day}>
          <h2 className={s.dayTitle}>{formatDate(day, i18n.language)}</h2>
          <Panel>
            <ul className={s.list}>
              {list.map((e) => (
                <LogItem key={e.id} event={e} />
              ))}
            </ul>
          </Panel>
        </section>
      ))}
    </>
  );
}

function LogHeader({ title, meta, pdfUrl }: { title: string; meta: string[]; pdfUrl: string }) {
  const { t } = useTranslation();
  const back = useLocation().pathname.startsWith('/vuokranantaja')
    ? '/vuokranantaja/tili'
    : '/vuokralainen/tili';
  return (
    <>
      <PageHeader
        breadcrumb={
          <>
            <Link to={back}>{t('account.title')}</Link>
            <ChevronRight size={14} aria-hidden="true" />
            <span aria-current="page">{t('log.title')}</span>
          </>
        }
        title={title}
        subtitle={meta.filter(Boolean).join(' · ')}
        actions={
          <a className={buttonClass('primary')} href={pdfUrl} download>
            <Download size={16} aria-hidden="true" />
            {t('log.downloadPdf')}
          </a>
        }
      />
      <p className={s.intro}>{t('log.intro')}</p>
    </>
  );
}

/** Koko asunnon loki (vuokranantaja): kaikki vuokrasuhteet ja huoltomerkinnät. */
export function PropertyLogPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { data: property } = useProperty(id);
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useEvents(id);
  if (isLoading) return <Splash />;
  const events = data?.pages.flatMap((page) => page.events) ?? [];
  return (
    <div>
      <LogHeader
        title={t('log.propertyTitle', { property: property ? propertyTitle(property) : '' })}
        meta={[]}
        pdfUrl={propertyLogPdfUrl(id)}
      />
      {events.length === 0 ? (
        <EmptyState icon={<ScrollText size={22} />} title={t('log.empty')} />
      ) : (
        <LogEvents events={events} />
      )}
      {hasNextPage && (
        <Button disabled={isFetchingNextPage} onClick={() => void fetchNextPage()}>
          {t('log.older')}
        </Button>
      )}
    </div>
  );
}

/** Vuokrasuhteen loki: vuokranantaja tai vuokralainen. */
export function LeaseLogPage() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const { data, isLoading, isError } = useLeaseLog(id);
  if (isLoading) return <Splash />;
  if (isError || !data) {
    return <EmptyState icon={<ScrollText size={22} />} title={t('log.notAvailable')} />;
  }
  const period = `${formatDate(data.startDate, i18n.language)} – ${
    data.endDate ? formatDate(data.endDate, i18n.language) : t('invoices.untilFurther')
  }`;
  return (
    <div>
      <LogHeader
        title={t('log.leaseTitle', { property: data.propertyTitle })}
        meta={[data.address, period, data.tenants.join(', ')]}
        pdfUrl={leaseLogPdfUrl(id)}
      />
      {data.access === 'party' && <p className={s.intro}>{t('log.tenantScope')}</p>}
      {data.events.length === 0 ? (
        <EmptyState icon={<ScrollText size={22} />} title={t('log.empty')} />
      ) : (
        <LogEvents events={data.events} />
      )}
    </div>
  );
}

function LogItem({ event: e }: { event: PropertyEvent }) {
  const { t, i18n } = useTranslation();
  const { title, detail } = describe(e, t, i18n.language);
  const time = new Date(e.createdAt).toLocaleTimeString(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <li className={s.item}>
      <span className={s.time}>{time}</span>
      <div className={s.body}>
        <span className={s.title}>{title}</span>
        {detail}
        {e.reason && (
          <p className={s.reason}>
            <strong>{t('log.reason')}:</strong> {e.reason}
          </p>
        )}
        <span className={s.actor}>{e.actorName ?? t('log.system')}</span>
      </div>
    </li>
  );
}

function localDay(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

type Details = Record<string, unknown>;
const str = (v: unknown) => (typeof v === 'string' ? v : '');
const num = (v: unknown) => (typeof v === 'number' ? v : null);

/** Kirjauksen otsikko ja lisätiedot luettavassa muodossa. */
function describe(
  e: PropertyEvent,
  t: TFunction,
  lang: string,
): { title: string; detail?: ReactNode } {
  const d: Details = e.details;
  const money = (v: unknown) => (num(v) === null ? '–' : formatCents(num(v)!, lang));
  const date = (v: unknown) => (str(v) ? formatDate(str(v), lang) : '–');
  const line = (text: string) => <span className={s.detail}>{text}</span>;
  const month = (v: unknown) => (str(v) ? monthLabel(str(v).slice(0, 7), lang) : '–');

  switch (e.type) {
    case 'lease.activated':
      return {
        title: t('log.type.leaseActivated'),
        detail: line(
          t('log.leaseActivatedDetail', {
            rent: money(d.rentCents),
            deposit: money(d.depositCents),
            start: date(d.startDate),
          }),
        ),
      };
    case 'lease.ended':
      return {
        title: t('log.type.leaseEnded'),
        detail: line(
          t('log.depositDetail', {
            returned: money(d.depositReturnedCents),
            deposit: money(d.depositCents),
            date: date(d.depositReturnedOn),
          }) + (str(d.note) ? ` (${str(d.note)})` : ''),
        ),
      };
    case 'lease.contract':
      return {
        title: str(d.name)
          ? t('log.type.contractLinked', { name: str(d.name) })
          : t('log.type.contractUnlinked'),
      };
    case 'settings.changed': {
      const changes = Array.isArray(d.changes) ? (d.changes as Details[]) : [];
      return {
        title: t('log.type.settingsChanged'),
        detail: line(
          changes
            .map(
              (c) =>
                `${t(`settings.${str(c.setting)}.title`)}: ${c.new ? t('settings.on') : t('settings.off')}`,
            )
            .join(' · '),
        ),
      };
    }
    case 'lease.deposit_deduction':
      return {
        title: t('log.type.depositDeduction', { amount: money(d.amountCents) }),
        detail: line(`${t(`deposit.kind.${str(d.kind)}`)}: ${str(d.description)}`),
      };
    case 'lease.terminated':
      return {
        title: t('log.type.leaseTerminated'),
        detail: line(
          t('log.terminatedDetail', {
            by: t(`ending.by.${str(d.by)}`),
            notice: date(d.noticeOn),
            end: date(d.endDate),
          }),
        ),
      };
    case 'lease.termination_cancelled':
      return { title: t('log.type.terminationCancelled') };
    case 'lease.pets':
      return { title: t(`log.pets.${str(d.new) || 'not_allowed'}`) };
    case 'lease.terms_changed': {
      const changes = Array.isArray(d.changes) ? (d.changes as Details[]) : [];
      return {
        title: t('log.type.termsChanged'),
        detail: (
          <ul className={s.changes}>
            {changes.map((c) => (
              <li key={str(c.field)}>
                {t(`log.field.${str(c.field)}`)}: {fieldValue(c.field, c.old, t, lang)} →{' '}
                {fieldValue(c.field, c.new, t, lang)}
              </li>
            ))}
          </ul>
        ),
      };
    }
    case 'party.invited':
      return { title: t('log.type.partyInvited', { name: str(d.name) }) };
    case 'party.joined':
      return { title: t('log.type.partyJoined', { name: str(d.name) }) };
    case 'inspection.started':
    case 'inspection.submitted':
    case 'inspection.approved':
    case 'inspection.returned':
      return {
        title: t(`log.type.${e.type.replace('.', '_')}`, {
          what: t(`log.inspection.${str(d.inspectionType) || 'move_in'}`),
        }),
        detail: str(d.comment) ? line(`”${str(d.comment)}”`) : undefined,
      };
    case 'thread.created':
      return {
        title:
          d.kind === 'fault'
            ? t('log.type.faultCreated', { subject: str(d.subject) })
            : t('log.type.threadCreated', { subject: str(d.subject) }),
      };
    case 'fault.status':
      return {
        title: t('log.type.faultStatus', {
          old: t(`faultStatus.${str(d.old)}`),
          new: t(`faultStatus.${str(d.new)}`),
        }),
        detail: line(str(d.subject)),
      };
    case 'message.sent':
      return {
        title: t('log.type.messageSent', { subject: str(d.subject) }),
        detail: line(`”${str(d.excerpt)}”`),
      };
    case 'occupant.added':
    case 'occupant.removed': {
      const pet = d.kind === 'pet';
      const name = str(d.name) + (pet && str(d.species) ? ` (${str(d.species)})` : '');
      const key = `${pet ? 'pet' : 'occupant'}${e.type.endsWith('added') ? 'Added' : 'Removed'}`;
      return { title: t(`log.type.${key}`, { name }) };
    }
    case 'maintenance.added':
    case 'maintenance.updated':
    case 'maintenance.removed':
      return {
        title: t(`log.type.${e.type.replace('.', '_')}`, { title: str(d.title) }),
        detail: str(d.happenedOn) ? line(date(d.happenedOn)) : undefined,
      };
    case 'file.added':
    case 'file.removed':
      return {
        title: t(`log.type.${e.type.replace('.', '_')}`, { name: str(d.name) }),
        detail: str(d.folder) ? line(t(`folders.${str(d.folder)}`)) : undefined,
      };
    case 'invoice.template_added':
    case 'invoice.template_removed':
      return {
        title: t(`log.type.${e.type.replace('.', '_')}`, {
          from: month(d.startMonth),
          to: str(d.endMonth) ? month(d.endMonth) : t('invoices.untilFurther'),
        }),
        detail: line(
          `${t('lease.perMonth', { amount: money(d.totalCents) })} · ${t(
            d.format === 'pdf' ? 'invoices.formatPdf' : 'invoices.formatApp',
          )}`,
        ),
      };
    case 'invoice.template_changed': {
      const parts: string[] = [];
      if (d.oldEnd !== d.newEnd) {
        parts.push(
          t('log.templateEnd', {
            old: str(d.oldEnd) ? month(d.oldEnd) : t('invoices.untilFurther'),
            new: str(d.newEnd) ? month(d.newEnd) : t('invoices.untilFurther'),
          }),
        );
      }
      if (d.oldDueDay !== d.newDueDay) {
        parts.push(t('log.templateDue', { old: d.oldDueDay, new: d.newDueDay }));
      }
      return {
        title: t('log.type.invoice_template_changed', { from: month(d.startMonth) }),
        detail: line(parts.join(' · ')),
      };
    }
    case 'expense.added':
    case 'expense.updated':
    case 'expense.removed':
      return {
        title: t(`log.type.${e.type.replace('.', '_')}`, { name: str(d.description) }),
        detail: line(
          `${t(`expenses.category.${str(d.category)}`)} · ${
            num(d.oldAmountCents) !== null && d.oldAmountCents !== d.amountCents
              ? `${money(d.oldAmountCents)} → ${money(d.amountCents)}`
              : money(d.amountCents)
          } · ${date(d.paidOn)}`,
        ),
      };
    case 'expense.template_added':
    case 'expense.template_changed':
    case 'expense.template_removed':
      return {
        title: t(`log.type.${e.type.replace('.', '_')}`, { name: str(d.description) }),
        detail: line(
          `${t('lease.perMonth', { amount: money(d.amountCents) })} · ${month(d.startMonth)} – ${
            str(d.endMonth) ? month(d.endMonth) : t('invoices.untilFurther')
          }`,
        ),
      };
    case 'invoice.paid':
    case 'invoice.payment_removed':
      return {
        title: t(`log.type.${e.type.replace('.', '_')}`, { month: month(d.period) }),
        detail: line(
          t('log.paymentDetail', { amount: money(d.amountCents), date: date(d.paidOn) }),
        ),
      };
    case 'invoice.reported':
    case 'invoice.report_rejected':
    case 'invoice.report_cancelled':
      return {
        title: t(`log.type.${e.type.replace('.', '_')}`, { month: month(d.period) }),
        detail: line(t('log.reportedDetail', { date: date(d.paidOn) })),
      };
    case 'invoice.edited':
      return {
        title: t('log.type.invoice_edited', { month: month(d.period) }),
        detail: line(
          `${money(d.oldTotal)} → ${money(d.newTotal)}` + (str(d.note) ? ` (${str(d.note)})` : ''),
        ),
      };
    default:
      return { title: e.type };
  }
}

function fieldValue(field: unknown, value: unknown, t: TFunction, lang: string): string {
  if (value === null || value === undefined || value === '') {
    return field === 'endDate' ? t('lease.indefinite') : '–';
  }
  switch (field) {
    case 'rentCents':
    case 'waterCents':
      return num(value) === null ? '–' : formatCents(num(value)!, lang);
    case 'dueDay':
      return t('lease.dueDayValue', { day: value });
    case 'waterBilling':
      return t(`lease.water.${str(value)}`);
    case 'electricityBilling':
      return t(`lease.electricity.${str(value)}`);
    case 'endDate':
      return formatDate(str(value), lang);
    default:
      return String(value);
  }
}
